import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, TrendingUp, Gift, CheckCircle2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const COOKIE_TTL_DAYS = 30;

/**
 * Affiliate landing route: /r/:code
 * - Validates the code against backend
 * - Sets a 30-day cookie (localStorage) so signup/checkout can pick it up
 * - Fires a click-tracking event
 * - Redirects to the register/subscribe page after a short delay
 */
export default function AffiliateLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | ok | invalid
  const [affiliateName, setAffiliateName] = useState('');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!code) return;
      try {
        const { data } = await axios.get(`${API}/affiliates/validate/${encodeURIComponent(code)}`);
        if (cancelled) return;
        if (!data.valid) {
          setStatus('invalid');
          setTimeout(() => navigate('/register'), 1500);
          return;
        }
        setAffiliateName(data.name || '');

        // Persist code with a 30-day expiry (localStorage acts as our tracking cookie)
        const expiresAt = new Date(Date.now() + COOKIE_TTL_DAYS * 24 * 3600 * 1000).toISOString();
        localStorage.setItem('tradex_affiliate', JSON.stringify({ code: data.code, expires_at: expiresAt, name: data.name }));

        // Fire click event (best-effort, non-blocking)
        axios.post(`${API}/affiliates/track-click`, {
          code: data.code,
          landing_url: window.location.href,
        }).catch(() => {});

        setStatus('ok');
        // Redirect to the signup page after showing confirmation briefly
        setTimeout(() => navigate('/register'), 1800);
      } catch (_e) {
        if (cancelled) return;
        setStatus('invalid');
        setTimeout(() => navigate('/register'), 1500);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [code, navigate]);

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4">
      <div className="w-full max-w-lg text-center space-y-6 animate-fade-in" data-testid="affiliate-landing">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10">
          <TrendingUp className="w-8 h-8 text-primary" />
        </div>

        {status === 'checking' && (
          <>
            <h1 className="text-2xl font-bold">Vérification du code…</h1>
            <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
          </>
        )}

        {status === 'ok' && (
          <>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs uppercase tracking-wide">
              <CheckCircle2 className="w-4 h-4" /> Code validé
            </div>
            <h1 className="text-3xl font-bold" data-testid="affiliate-welcome-title">
              Bienvenue !
              {affiliateName ? <> Recommandé par <span className="text-primary">{affiliateName}</span></> : null}
            </h1>
            <div className="glass-card p-6 text-left space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                <Gift className="w-4 h-4" /> Ton avantage exclusif
              </div>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• <strong className="text-foreground">-10 %</strong> sur ta première souscription</li>
                <li>• <strong className="text-foreground">50 analyses IA offertes</strong> avec l&apos;abonnement Pro mensuel</li>
                <li>• <strong className="text-foreground">+3 assets Scan Auto</strong> avec l&apos;abonnement Expert mensuel</li>
              </ul>
            </div>
            <p className="text-xs text-muted-foreground">Redirection vers la création de compte…</p>
            <Loader2 className="w-5 h-5 animate-spin text-primary mx-auto" />
          </>
        )}

        {status === 'invalid' && (
          <>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 text-red-400 text-xs uppercase tracking-wide">
              <XCircle className="w-4 h-4" /> Code invalide
            </div>
            <h1 className="text-xl font-semibold">Ce lien d&apos;affiliation n&apos;est pas valide</h1>
            <p className="text-sm text-muted-foreground">Redirection vers l&apos;inscription standard…</p>
          </>
        )}
      </div>
    </div>
  );
}
